/**
 * The table and the filter bar the whole workspace is built on.
 *
 * ONE table component (`DataTable`), not one per screen — built on MUI's
 * Table, generic over the row type. See docs/ui-design.md.
 *
 * Wide content scrolls inside its own container. Columns are never dropped to
 * avoid a scrollbar — an auditor who cannot see the exception count has not
 * been helped by the layout that hid it.
 */

import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Popover,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { DatePicker, Select } from '../../shared/fields';

/* ═══ a table, and the room to read it ═════════════════════════════════════ */

/**
 * The scroll container every table sits in, and its way out.
 *
 * A fixed height is right until the table IS what the reader came for, so each
 * carries a button that takes it to the whole viewport. `createPortal` keeps
 * the same component instance, so a sort or selection survives the trip.
 *
 * Not a modal: no scrim, no dialog role. The same table, given the screen.
 */
/** The expand glyph, at the size a button gives it. */
function ExpandIcon() {
	return (
		<svg className="i" viewBox="0 0 14 14" fill="none" aria-hidden="true">
			<path
				d="M5 1H1v4M9 13h4V9M13 5V1H9M1 9v4h4"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/**
 * Full screen for one table, as two pieces a caller places separately: `button`
 * in the card header where every other action lives, `frame` around the table.
 */
/** What `useFullScreen` hands back, split so a card can place each piece. */
export interface FullScreen {
	open: boolean;
	button: ReactNode;
	frame: (children: ReactNode) => ReactNode;
}

export function useFullScreen(
	title: string,
	options?: {
		/** An icon-only button, for a toolbar that has no room for the labelled one. */
		compact?: boolean;
	},
): FullScreen {
	/* Where the full-screen view is mounted while open: the page — or, for a
     table inside a dialog, that dialog, so the view covers the screen above
     the dialog and inside its focus rather than underneath it. */
	const [host, setHost] = useState<HTMLElement | null>(null);
	const open = host !== null;
	/* Focus moves into the view once, as it opens, so Escape reaches it. */
	const focusView = useCallback(
		(node: HTMLDivElement | null) => node?.focus(),
		[],
	);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setHost(null);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);

	const enter = (element: HTMLElement) =>
		setHost(
			element.closest<HTMLElement>('.MuiDialog-container') ??
				document.body,
		);

	const button = options?.compact ? (
		<Tooltip title="Full screen">
			<IconButton
				size="small"
				aria-label={`View ${title} full screen`}
				onClick={(event) => enter(event.currentTarget)}
			>
				<ExpandIcon />
			</IconButton>
		</Tooltip>
	) : (
		<Button
			variant="outlined"
			size="small"

			title={`View ${title} full screen`}
			onClick={(event) => enter(event.currentTarget)}
		>
			<Box pr={1}>
				<ExpandIcon />
			</Box>
			Full screen
		</Button>
	);

	/* Portalled, not remounted: the table keeps its place in the React tree, so
     a sort or a scroll position survives the trip out and back. */
	const frame = (children: ReactNode) =>
		host
			? createPortal(
					<div
						className="fsview"
						ref={focusView}
						tabIndex={-1}
						/* Escape leaves full screen only — inside a dialog it would
               otherwise also close the dialog. */
						onKeyDown={(event) => {
							if (event.key !== 'Escape') return;
							event.stopPropagation();
							setHost(null);
						}}
					>
						<header className="fsview-hd">
							<h2>{title}</h2>
							<Button
								variant="outlined"
								size="small"
								onClick={() => setHost(null)}
							>
								Close
							</Button>
						</header>
						<div className="fsview-bd">{children}</div>
					</div>,
					host,
				)
			: children;

	return { open, button, frame };
}

/* The bar above a table (pager, full screen): the same space above and
   below, and the pager's own MUI padding taken out so it sits level. */
const BAR_SX = {
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: 'var(--s2)',
	px: 'var(--s3)',
	py: 'var(--s2)',
	'& .MuiTablePagination-toolbar': { minHeight: 0, py: 0 },
	'& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
		m: 0,
	},
	'@media print': { display: 'none' },
} as const;

/**
 * The three heights a scrolling table can be. Named, not numbered: the number
 * lives in `tokens.css` as `--table-h`, where a designer can find it.
 *
 *   main    — a workspace tab's own table, the thing the screen is for
 *   compact — inside a wizard step or a popup, where a footer must stay put
 *   short   — a table with other content under it on the same screen
 *
 * A raw number still works for the one-off that needs it.
 */
export type TableHeight = 'main' | 'compact' | 'short';

const TABLE_CAP: Record<TableHeight, string> = {
	main: 'var(--table-h)',
	compact: 'var(--table-h-compact)',
	short: 'var(--table-h-short)',
};

/**
 * How short, and how tall, each size is allowed to get once it has measured
 * the room it actually has.
 *
 * A flat 420px is wrong in both directions and there is no third number that
 * is right: on a 1050px window it leaves a third of the screen white under
 * the table, and on a wizard step with a footer under it, it is what pushes
 * the page into scrolling. So the table asks the page how much room is left
 * and takes it, between these two — see `useFitHeight`.
 */
const TABLE_BOUNDS: Record<TableHeight, { min: number; max: number }> = {
	main: { min: 250, max: 760 },
	compact: { min: 220, max: 480 },
	short: { min: 140, max: 340 },
};

/** Left under the table: a card's own footer padding, and air. */
const BOTTOM_AIR = 20;

/**
 * The height that makes this table end where the page ends.
 *
 * Measured, not guessed. The page root's `scrollHeight` less this table's own
 * height is everything else on the screen — headers, filter bars, the card's
 * footer, whatever sits under it — so what is left of the window after that
 * is exactly what the table can have. It converges in one pass because the
 * table's own height is subtracted out before the room is worked out.
 *
 * Falls back to the fixed cap when there is no page root to measure (a table
 * inside a popup, say), which is why the CSS formula is still there.
 */
function useFitHeight(
	enabled: boolean,
	box: React.RefObject<HTMLElement>,
	bounds: { min: number; max: number } | null,
) {
	const [fit, setFit] = useState<number | null>(null);

	const measure = useCallback(() => {
		if (!enabled || !bounds) return setFit(null);
		const el = box.current;
		const root = el?.closest('.wrap') as HTMLElement | null;
		if (!el || !root) return setFit(null);

		const rest = root.scrollHeight - el.getBoundingClientRect().height;
		const room =
			window.innerHeight -
			root.getBoundingClientRect().top -
			rest -
			BOTTOM_AIR;
		setFit(Math.round(Math.min(bounds.max, Math.max(bounds.min, room))));
	}, [enabled, bounds, box]);

	useLayoutEffect(measure, [measure]);

	useEffect(() => {
		if (!enabled) return;
		const el = box.current;
		const root = el?.closest('.wrap');
		if (!root) return;

		/* The page changes height for reasons that are not a resize — a filter
       bar opening, a note appearing, rows arriving — so watch the page, not
       just the window. Our own change to the table cancels out of `rest`, so
       this settles rather than chasing itself. */
		const observer = new ResizeObserver(() =>
			requestAnimationFrame(measure),
		);
		observer.observe(root);
		window.addEventListener('resize', measure);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [enabled, measure, box]);

	return fit;
}

export function TableScroll({
	title,
	maxHeight,
	className = '',
	fullScreen,
	toolbar,
	children,
}: {
	/** What the reader is looking at, shown as the full-screen heading. */
	title: string;
	maxHeight?: TableHeight | number;
	/** Extra classes for the scroll container — `previewgrid`, for one. */
	className?: string;
	/**
	 * Pass one from `useFullScreen` when the caller has already placed the
	 * button — in a card header or a filter row. The bar above the table is
	 * then not drawn, because the control is somewhere better.
	 */
	fullScreen?: FullScreen;
	/** Controls drawn in the bar above the table — the pager. Shown in full
	 *  screen too, so paging never needs a trip back out. */
	toolbar?: ReactNode;
	children: ReactNode;
}) {
	const own = useFullScreen(title);
	const { open: full, button, frame } = fullScreen ?? own;

	const box = useRef<HTMLDivElement>(null);
	const bounds =
		typeof maxHeight === 'string' ? TABLE_BOUNDS[maxHeight] : null;
	const fit = useFitHeight(!full && Boolean(maxHeight), box, bounds);

	const body = (
		<div
			ref={box}
			className={`tblwrap ${className}`.trim()}
			/* A ceiling AND a floor on the viewport.
         A flat 420px is right on a laptop and leaves a 27-inch screen mostly
         empty, and on a short window it is the reason the PAGE scrolls as
         well as the table — two scrollbars, one of which moves the thing you
         are reading out from under the other. `min()` takes whichever is
         smaller, so the table gives the page back its height instead. */
			style={
				full
					? { maxHeight: 'none' }
					: fit !== null
						? { maxHeight: fit, overflowY: 'auto' }
						: maxHeight
							? {
									maxHeight: `min(${
										typeof maxHeight === 'number'
											? `${maxHeight}px`
											: TABLE_CAP[maxHeight]
									}, calc(100dvh - var(--table-reserve)))`,
									overflowY: 'auto',
								}
							: undefined
			}
		>
			{children}
		</div>
	);

	const bar = toolbar ? <Box sx={BAR_SX}>{toolbar}</Box> : null;

	if (full)
		return frame(
			/* Takes the full-screen panel's height and hands it to the table, or
         the table grows to its full length and the panel clips it. */
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flex: '1 1 auto',
					minHeight: 0,
				}}
			>
				{bar}
				{body}
			</Box>,
		);
	/* The caller placed Full screen itself (a filter row, a card header): the
     bar above the table carries only the pager. */
	if (fullScreen)
		return bar ? (
			<Box sx={{ display: 'grid' }}>
				{bar}
				{body}
			</Box>
		) : (
			body
		);

	return (
		<Box sx={{ display: 'grid' }}>
			{/* Standing, not on hover: a control that appears when the pointer
          arrives is a control most readers never learn about. */}
			<Box sx={BAR_SX}>
				{button}
				{toolbar}
			</Box>
			{body}
		</Box>
	);
}

/* ═══ the data table ═══════════════════════════════════════════════════════ */

export interface Column<T> {
	key: string;
	label: ReactNode;
	/** Right-aligned, tabular figures. */
	num?: boolean;
	/** Header and cells aligned this way. Default: right for `num`, else left. */
	align?: 'left' | 'center' | 'right';
	width?: number;
	/** A second line under the header: the unit, or the qualifier. */
	note?: string;
	/** Sortable when given: the comparable value for this row. */
	sortValue?: (row: T) => string | number;
	/** Sortable by the server when the table is given `sort` and `onSortChange`
	 *  alongside `paging`. */
	sortable?: boolean;
	/** `index` is the row's 1-based position in the list as shown (paging
	 *  included), for a "#" column on result tables. */
	render: (row: T, index: number) => ReactNode;
	className?: string;
}

/** Paging owned by the caller — a server-paged result. */
export interface TablePaging {
	/** 1-based. */
	page: number;
	pageSize: number;
	/** Rows across every page. */
	total: number;
	onPageChange: (page: number) => void;
	onPageSizeChange?: (size: number) => void;
	/** The page sizes offered. Defaults to 50 / 100 / 500. */
	pageSizes?: number[];
}

/** Sorting owned by the caller — the server orders the whole result. */
export type ServerSort = { key: string; direction: 'asc' | 'desc' } | null;

/** Above this many in-memory rows the table pages itself. */
const CLIENT_PAGE_THRESHOLD = 500;
const PAGE_SIZES = [50, 100, 500];

/**
 * The one table. Every list and result in the product uses it.
 *
 * Sentence-case headers, numbers right-aligned in tabular figures, sortable
 * headers with `aria-sort`, a sticky header inside its own scroll container
 * (never a page-level horizontal scroll), an honest row count, skeleton rows
 * while loading, the empty state inside the table body, and a totals footer.
 * Paging: pass `paging` for a server-paged result; an in-memory list over 500
 * rows pages itself.
 */
export function DataTable<T>({
	rows,
	columns,
	rowKey,
	onRowClick,
	empty,
	totals,
	caption,
	initialSort,
	maxHeight = 'main',
	selected,
	selection,
	rowClass,
	fullScreen,
	loading = false,
	paging,
	sort: serverSort,
	onSortChange,
	refreshing,
}: {
	rows: T[];
	columns: Array<Column<T>>;
	rowKey: (row: T) => string;
	onRowClick?: (row: T) => void;
	/** Tints a ticked row so the selection reads at a glance. */
	selected?: (row: T) => boolean;
	/** Checkbox selection, keyed by `rowKey`. Draws a checkbox column first. */
	selection?: {
		selected: ReadonlySet<string>;
		onChange: (next: Set<string>) => void;
	};
	rowClass?: (row: T) => string | undefined;
	/** Shown inside the table body when there are no rows. */
	empty?: ReactNode;
	totals?: Record<string, ReactNode>;
	caption?: string;
	initialSort?: { key: string; direction: 'asc' | 'desc' };
	maxHeight?: TableHeight | number;
	fullScreen?: FullScreen;
	loading?: boolean;
	paging?: TablePaging;
	/** With `paging` and `onSortChange`: the sort the server applied. Headers of
	 *  columns marked `sortable` (or with a `sortValue`) then sort the whole
	 *  result, not one page. `null` means source order. */
	sort?: ServerSort;
	onSortChange?: (sort: ServerSort) => void;
	/** A thin progress line under the header while a refetch is under way. */
	refreshing?: boolean;
}) {
	const [sort, setSort] = useState<{
		key: string;
		direction: 'asc' | 'desc';
	} | null>(initialSort ?? null);
	const [ownPage, setOwnPage] = useState(1);
	const [ownSize, setOwnSize] = useState(100);

	const sorted = useMemo(() => {
		if (!sort || paging) return rows;
		const column = columns.find((item) => item.key === sort.key);
		if (!column?.sortValue) return rows;
		const get = column.sortValue;
		const factor = sort.direction === 'asc' ? 1 : -1;
		return [...rows].sort((a, b) => {
			const left = get(a);
			const right = get(b);
			if (typeof left === 'number' && typeof right === 'number')
				return (left - right) * factor;
			return (
				String(left).localeCompare(String(right), 'en-IN', {
					numeric: true,
				}) * factor
			);
		});
	}, [rows, columns, sort, paging]);

	const selfPaged = !paging && rows.length > CLIENT_PAGE_THRESHOLD;
	const page = paging ? paging.page : ownPage;
	const pageSize = paging ? paging.pageSize : ownSize;
	const total = paging ? paging.total : rows.length;
	const offset = paging || selfPaged ? (page - 1) * pageSize : 0;
	const visible = selfPaged
		? sorted.slice(offset, offset + pageSize)
		: sorted;

	const shownKeys = selection ? visible.map(rowKey) : [];
	const allOn =
		shownKeys.length > 0 &&
		shownKeys.every((key) => selection!.selected.has(key));
	const someOn = shownKeys.some((key) => selection!.selected.has(key));
	const toggleAll = () => {
		if (!selection) return;
		const next = new Set(selection.selected);
		for (const key of shownKeys) {
			if (allOn) next.delete(key);
			else next.add(key);
		}
		selection.onChange(next);
	};
	const toggleOne = (key: string) => {
		if (!selection) return;
		const next = new Set(selection.selected);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		selection.onChange(next);
	};
	const span = columns.length + (selection ? 1 : 0);

	/* The declared widths, summed: the scroll container then scrolls sideways
     instead of crushing a column into a tower of wrapped words. */
	const minWidth =
		columns.reduce((sum, column) => sum + (column.width ?? 150), 0) +
		(selection ? 40 : 0);

	const controlledSort = Boolean(paging && onSortChange);
	const shownSort = controlledSort ? (serverSort ?? null) : sort;

	/* Ascending, then descending, then back to the source order. */
	const toggleSort = (key: string) => {
		if (controlledSort) {
			if (shownSort?.key !== key)
				onSortChange?.({ key, direction: 'asc' });
			else if (shownSort.direction === 'asc')
				onSortChange?.({ key, direction: 'desc' });
			else onSortChange?.(null);
			return;
		}
		setSort((current) =>
			current?.key === key
				? {
						key,
						direction: current.direction === 'asc' ? 'desc' : 'asc',
					}
				: { key, direction: 'asc' },
		);
	};

	/* Rows per page and the page controls, in the bar above the table. */
	const pager =
		(paging || selfPaged) && total > 0 ? (
			<TablePagination
				component="div"
				count={total}
				page={page - 1}
				rowsPerPage={pageSize}
				rowsPerPageOptions={paging?.pageSizes ?? PAGE_SIZES}
				labelRowsPerPage="Rows per page"
				labelDisplayedRows={({ from, to, count }) =>
					`Rows ${from.toLocaleString('en-IN')}–${to.toLocaleString('en-IN')} of ${count.toLocaleString('en-IN')}`
				}
				onPageChange={(_event, next) =>
					paging
						? paging.onPageChange(next + 1)
						: setOwnPage(next + 1)
				}
				onRowsPerPageChange={(event) => {
					const size = Number(event.target.value);
					if (paging) paging.onPageSizeChange?.(size);
					else {
						setOwnSize(size);
						setOwnPage(1);
					}
				}}
			/>
		) : null;

	return (
		<>
			{refreshing !== undefined && (
				/* A fixed-height slot, so the bar appearing never moves the table. */
				<Box sx={{ height: 2 }}>
					{refreshing && (
						<LinearProgress
							aria-label="Refreshing the rows"
							sx={{ height: 2 }}
						/>
					)}
				</Box>
			)}
			<TableScroll
				title={caption ?? 'Table'}
				maxHeight={maxHeight}
				fullScreen={fullScreen}
				toolbar={pager}
			>
				<Table
					stickyHeader
					size="small"
					sx={{ minWidth }}
					aria-label={caption}
				>
					{caption && (
						<caption className="sr-only">{caption}</caption>
					)}
					<TableHead>
						<TableRow>
							{selection && (
								<TableCell
									padding="checkbox"
									sx={{ verticalAlign: 'middle' }}
								>
									<Checkbox
										size="small"
										checked={allOn}
										indeterminate={someOn && !allOn}
										disabled={shownKeys.length === 0}
										inputProps={{
											'aria-label':
												'Select every row shown',
										}}
										onChange={toggleAll}
									/>
								</TableCell>
							)}
							{columns.map((column) => {
								/* Sorting a server-paged result would only sort one page, so
                   it is offered only when the table holds every row. */
								const sortable = controlledSort
									? Boolean(
											column.sortable || column.sortValue,
										)
									: Boolean(column.sortValue) && !paging;
								const active = shownSort?.key === column.key;
								return (
									<TableCell
										key={column.key}
										align={
											column.align ??
											(column.num ? 'right' : 'left')
										}
										className={column.className}
										sx={
											column.width
												? { width: column.width }
												: undefined
										}
										sortDirection={
											active && sortable
												? shownSort?.direction
												: false
										}
									>
										{sortable ? (
											<TableSortLabel
												active={active}
												direction={
													active
														? shownSort?.direction
														: 'asc'
												}
												onClick={() =>
													toggleSort(column.key)
												}
											>
												{column.label}
											</TableSortLabel>
										) : (
											column.label
										)}
										{column.note && (
											<span className="th-note">
												{column.note}
											</span>
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && rows.length === 0 ? (
							Array.from({ length: 8 }, (_, index) => (
								<TableRow key={`skeleton-${index}`}>
									{selection && (
										<TableCell padding="checkbox" />
									)}
									{columns.map((column) => (
										<TableCell key={column.key}>
											<Skeleton variant="text" />
										</TableCell>
									))}
								</TableRow>
							))
						) : visible.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={span}
									sx={{
										py: 'var(--s6)',
										textAlign: 'center',
									}}
								>
									{empty ?? (
										<span className="mut">
											Nothing to show.
										</span>
									)}
								</TableCell>
							</TableRow>
						) : (
							visible.map((row, index) => (
								<TableRow
									key={rowKey(row)}
									hover={Boolean(onRowClick)}
									selected={
										(selection?.selected.has(rowKey(row)) ??
											false) ||
										(selected?.(row) ?? false)
									}
									className={rowClass?.(row)}
									onClick={
										onRowClick
											? () => onRowClick(row)
											: undefined
									}
									tabIndex={onRowClick ? 0 : undefined}
									sx={
										onRowClick
											? { cursor: 'pointer' }
											: undefined
									}
									onKeyDown={
										onRowClick
											? (event) => {
													if (
														event.target !==
														event.currentTarget
													)
														return;
													if (
														event.key === 'Enter' ||
														event.key === ' '
													) {
														event.preventDefault();
														onRowClick(row);
													}
												}
											: undefined
									}
								>
									{selection && (
										<TableCell
											padding="checkbox"
											sx={{ verticalAlign: 'middle' }}
											onClick={(event) =>
												event.stopPropagation()
											}
										>
											<Checkbox
												size="small"
												checked={selection.selected.has(
													rowKey(row),
												)}
												inputProps={{
													'aria-label': 'Select row',
												}}
												onKeyDown={(event) =>
													event.stopPropagation()
												}
												onChange={() =>
													toggleOne(rowKey(row))
												}
											/>
										</TableCell>
									)}
									{columns.map((column) => (
										<TableCell
											key={column.key}
											align={
												column.align ??
												(column.num ? 'right' : 'left')
											}
											className={column.className}
										>
											{column.render(
												row,
												offset + index + 1,
											)}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
					{totals && visible.length > 0 && (
						<TableFooter>
							<TableRow>
								{selection && <TableCell padding="checkbox" />}
								{columns.map((column, index) => (
									<TableCell
										key={column.key}
										align={
											column.align ??
											(column.num ? 'right' : 'left')
										}
									>
										{totals[column.key] ??
											(index === 0 ? 'Total' : '')}
									</TableCell>
								))}
							</TableRow>
						</TableFooter>
					)}
				</Table>
			</TableScroll>
		</>
	);
}

/* ═══ filter bar ═══════════════════════════════════════════════════════════ */

export interface FilterSpec {
	key: string;
	label: string;
	/** `combo` is a select you can type into, with options the caller may fetch
	 *  as the text changes; `date` is the shared date picker
	 *  (value is ISO). */
	kind: 'select' | 'search' | 'chips' | 'threshold' | 'combo' | 'date';
	options?: Array<{ value: string; label: string }>;
	placeholder?: string;
	/** `combo`: told what has been typed, so the caller can narrow `options`. */
	onInputChange?: (text: string) => void;
	/** `date`: the earliest and latest date the picker offers. */
	min?: string;
	max?: string;
	/** Neighbouring specs with the same group (a date range) wrap together. */
	group?: string;
}

const FIELD_LABEL_SX = {
	position: 'static',
	transform: 'none',
	fontSize: 'var(--fs-small)',
	mb: '2px',
} as const;

function Dot({ colour }: { colour: string }) {
	return (
		<Box
			component="span"
			aria-hidden="true"
			sx={{
				width: 7,
				height: 7,
				borderRadius: '50%',
				bgcolor: colour,
				flex: '0 0 7px',
			}}
		/>
	);
}

function CountLine({
	dot,
	text,
	n,
}: {
	dot?: string;
	text: string;
	n?: number;
}) {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px',
				width: '100%',
			}}
		>
			{dot && <Dot colour={dot} />}
			<Typography
				component="span"
				noWrap
				sx={{ fontSize: 'inherit', flex: 1 }}
			>
				{text}
			</Typography>
			{n !== undefined && (
				<Typography
					component="span"
					sx={{ fontSize: 'inherit', color: 'var(--ink-3)' }}
				>
					{n.toLocaleString('en-IN')}
				</Typography>
			)}
		</Box>
	);
}

/**
 * A dropdown whose options carry a colour dot and a count. Used directly by
 * toolbars.
 */
export function CountSelect({
	id,
	label,
	value,
	onChange,
	allLabel,
	total,
	options,
	size = 'normal',
	inlineLabel = false,
}: {
	id: string;
	label: string;
	value: string;
	onChange: (next: string) => void;
	allLabel: string;
	total?: number;
	options: Array<{
		value: string;
		label: string;
		colour?: string;
		n?: number;
	}>;
	/** 'small' = var(--ctl-h-sm) high, for compact toolbars. */
	size?: 'normal' | 'small';
	/** Show the label inside the control ("Kind · All kinds") instead of above it. */
	inlineLabel?: boolean;
}) {
	const chosen = options.find((option) => option.value === value);
	const height = size === 'small' ? 'var(--ctl-h-sm)' : 'var(--ctl-h)';
	const prefix = inlineLabel ? (
		<Typography
			component="span"
			sx={{ fontSize: 'inherit', color: 'var(--ink-3)' }}
		>
			{label}:
		</Typography>
	) : null;
	return (
		<Box sx={{ minWidth: size === 'small' ? 170 : 220 }}>
			{!inlineLabel && (
				<InputLabel htmlFor={id} shrink sx={FIELD_LABEL_SX}>
					{label}
				</InputLabel>
			)}
			<TextField
				id={id}
				select
				size="small"
				fullWidth
				value={value}
				onChange={(event) => onChange(event.target.value)}
				sx={{
					'& .MuiOutlinedInput-root': { height },
					'& .MuiSelect-select': {
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						height: `${height} !important`,
						minHeight: `${height} !important`,
						boxSizing: 'border-box',
						py: '0 !important',
						fontSize: 'var(--fs-body)',
					},
				}}
				slotProps={{
					select: {
						displayEmpty: true,
						renderValue: () => (
							<>
								{prefix}
								{chosen ? (
									<CountLine
										dot={chosen.colour}
										text={chosen.label}
										n={inlineLabel ? undefined : chosen.n}
									/>
								) : (
									<CountLine
										text={allLabel}
										n={inlineLabel ? undefined : total}
									/>
								)}
							</>
						),
					},
					htmlInput: { 'aria-label': label },
				}}
			>
				<MenuItem value="">
					<CountLine text={allLabel} n={total} />
				</MenuItem>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						<CountLine
							dot={option.colour}
							text={option.label}
							n={option.n}
						/>
					</MenuItem>
				))}
			</TextField>
		</Box>
	);
}

/**
 * The one filter bar.
 *
 * The filters really filter, and the bar says how many
 * are on and offers one click to clear them — the state a reviewer most often
 * gets stuck in is "why is this table empty", and the answer is usually a
 * filter they forgot.
 */
export function FilterBar({
	specs,
	values,
	onChange,
	onClear,
	actions,
	resultNote,
	defaultOpen,
}: {
	specs: FilterSpec[];
	values: Record<string, string>;
	onChange: (key: string, value: string) => void;
	onClear: () => void;
	actions?: ReactNode;
	resultNote?: ReactNode;
	/** Show the non-search filters from the start; the toggle still hides them. */
	defaultOpen?: boolean;
}) {
	const active = specs.filter(
		(spec) => (values[spec.key] ?? '') !== '',
	).length;

	const [filterActive, setFilterActive] = useState(defaultOpen ?? false);

	/* Specs sharing a `group` render side by side in one box. */
	const grouped: Array<FilterSpec | FilterSpec[]> = [];
	for (const spec of specs) {
		const last = grouped[grouped.length - 1];
		if (spec.group && Array.isArray(last) && last[0]?.group === spec.group)
			last.push(spec);
		else grouped.push(spec.group ? [spec] : spec);
	}

	const renderSpec = (spec: FilterSpec) => {
		const value = values[spec.key] ?? '';
		if (spec.kind === 'chips') {
			return (
				<ToggleButtonGroup
					key={spec.key}
					size="small"
					exclusive
					value={value}
					aria-label={spec.label}
					onChange={(_event, next: string | null) =>
						onChange(spec.key, next ?? '')
					}
				>
					{(spec.options ?? []).map((option) => (
						<ToggleButton
							key={option.value}
							value={option.value}
							sx={{ textTransform: 'none' }}
						>
							{option.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			);
		}
		if (spec.kind === 'date') {
			return (
				<Box key={spec.key} sx={{ width: 168 }}>
					<InputLabel
						htmlFor={`f-${spec.key}`}
						shrink
						sx={FIELD_LABEL_SX}
					>
						{spec.label}
					</InputLabel>
					<DatePicker
						id={`f-${spec.key}`}
						value={value}
						min={spec.min}
						max={spec.max}
						ariaLabel={spec.label}
						placeholder={spec.placeholder}
						onChange={(next) => onChange(spec.key, next)}
					/>
				</Box>
			);
		}
		if (spec.kind === 'combo') {
			const options = spec.options ?? [];
			/* The chosen value stays selectable even when the typed text has
           narrowed the list past it. */
			const found = options.find((option) => option.value === value);
			const chosen = value ? (found ?? { value, label: value }) : null;
			return (
				<Box key={spec.key} sx={{ minWidth: 200 }}>
					<InputLabel
						htmlFor={`f-${spec.key}`}
						shrink
						sx={FIELD_LABEL_SX}
					>
						{spec.label}
					</InputLabel>
					<Autocomplete
						id={`f-${spec.key}`}
						size="small"
						options={
							chosen && !found ? [chosen, ...options] : options
						}
						value={chosen}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(a, b) => a.value === b.value}
						/* The caller narrows the list; filtering here too would hide
                 values the server matched on a different spelling. */
						filterOptions={(all) => all}
						onChange={(_event, next) =>
							onChange(spec.key, next?.value ?? '')
						}
						onInputChange={(_event, text, reason) => {
							if (reason === 'input') spec.onInputChange?.(text);
							else if (reason === 'clear' || reason === 'reset')
								spec.onInputChange?.('');
						}}
						noOptionsText="No matches"
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={spec.placeholder ?? 'All'}
								inputProps={{
									...params.inputProps,
									'aria-label': spec.label,
								}}
							/>
						)}
					/>
				</Box>
			);
		}
		if (spec.kind === 'select') {
			return (
				<Box key={spec.key} sx={{ minWidth: 180 }}>
					<InputLabel
						htmlFor={`f-${spec.key}`}
						shrink
						sx={{
							position: 'static',
							transform: 'none',
							fontSize: 'var(--fs-small)',
							mb: '2px',
						}}
					>
						{spec.label}
					</InputLabel>
					<Select
						id={`f-${spec.key}`}
						className="sm"
						value={value}
						options={spec.options ?? []}
						ariaLabel={spec.label}
						onChange={(next) => onChange(spec.key, next)}
					/>
				</Box>
			);
		}
		/* The label sits above the field like every other filter's, at 12px —
         MUI's floating label shrinks to 0.75em (10.5px), under the minimum. */
		return (
			<Box
				key={spec.key}
				sx={{
					flex: spec.kind === 'search' ? '1 1 240px' : undefined,
					maxWidth: 420,
				}}
			>
				<TextField
					id={`f-${spec.key}`}
					size="small"
					fullWidth
					type={spec.kind === 'threshold' ? 'number' : 'search'}
					value={value}
					placeholder={spec.placeholder}
					title={spec.placeholder}
					onChange={(event) => onChange(spec.key, event.target.value)}
				/>
			</Box>
		);
	};

	const searchSpecs = specs.filter((spec) => spec.kind === 'search');
	const hidden = grouped.filter(
		(item) => Array.isArray(item) || item.kind !== 'search',
	);
	const showFields = filterActive || searchSpecs.length > 0;

	return (
		/* One row: search (always) and the other filters (when shown) on the
       left, wrapping there; the count, Clear, Show / Hide and the caller's
       actions on the right. */
		<Box
			display="flex"
			alignItems="flex-end"
			gap="var(--s3)"
			flexWrap="wrap"
		>
			<Box
				display="flex"
				flexWrap="wrap"
				alignItems="flex-end"
				gap="var(--s3)"
				flex="1 1 320px"
				minWidth={0}
			>
				{searchSpecs.map(renderSpec)}
				{filterActive &&
					hidden.map((item) =>
						Array.isArray(item) ? (
							/* A range (from / to) wraps as one unit, never split across rows. */
							<Box
								key={item.map((spec) => spec.key).join('+')}
								display="flex"
								alignItems="flex-end"
								gap="var(--s3)"
							>
								{item.map(renderSpec)}
							</Box>
						) : (
							renderSpec(item)
						),
					)}
			</Box>
			{/* As tall as the filter boxes while they show (--ctl-h), so the
          buttons sit on the boxes' middle, not their bottom edge. */}
			<Box
				display="flex"
				alignItems="center"
				gap="var(--s2)"
				flexShrink={0}
				height={showFields ? 'var(--ctl-h)' : undefined}
			>
				{resultNote && (
					<Typography
						component="span"
						noWrap
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{resultNote}
					</Typography>
				)}
				{active > 0 && (
					<Button size="small" variant="text" onClick={onClear}>
						Clear {active} filter{active === 1 ? '' : 's'}
					</Button>
				)}
				{hidden.length > 0 && (
					<Button
						size="small"
						variant={filterActive ? 'contained' : 'outlined'}
						aria-expanded={filterActive}
						onClick={() => setFilterActive((shown) => !shown)}
					>
						{filterActive ? 'Hide filters' : 'Show filters'}
					</Button>
				)}
				{actions}
			</Box>
		</Box>
	);
}

/* ═══ column visibility ════════════════════════════════════════════════════ */

/**
 * "Columns" — which of a table's columns are shown.
 *
 * Generic: it knows nothing about what the columns are. The caller owns the
 * state (the choice is a view, not a saved setting) and passes the real column
 * list, so "49 of 64 columns" is always a count of something that exists.
 */
export function ColumnVisibility({
	columns,
	visible,
	defaultVisible,
	onChange,
}: {
	columns: Array<{ key: string; label: string }>;
	visible: string[];
	defaultVisible: string[];
	onChange: (next: string[]) => void;
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const shown = new Set(visible);
	/* Always in the table's own order, whatever order they were ticked in. */
	const toggle = (key: string, checked: boolean) =>
		onChange(
			columns
				.map((column) => column.key)
				.filter((other) =>
					other === key ? checked : shown.has(other),
				),
		);
	const sameAsDefault =
		defaultVisible.length === visible.length &&
		defaultVisible.every((key) => shown.has(key));

	return (
		<>
			<Button
				size="small"
				variant="outlined"
				aria-haspopup="dialog"
				aria-expanded={Boolean(anchor)}
				onClick={(event) => setAnchor(event.currentTarget)}
			>
				{visible.length < columns.length
					? `Columns · ${visible.length} of ${columns.length}`
					: 'Columns'}
			</Button>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box
					sx={{
						width: 280,
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
						<Typography variant="body2" color="text.secondary">
							{visible.length} of {columns.length} columns
						</Typography>
						<Box display="flex" gap={1} mt={0.5}>
							<Button
								size="small"
								variant="text"
								disabled={visible.length === columns.length}
								onClick={() =>
									onChange(
										columns.map((column) => column.key),
									)
								}
							>
								Show all
							</Button>
							<Button
								size="small"
								variant="text"
								disabled={sameAsDefault}
								onClick={() => onChange(defaultVisible)}
							>
								Reset to default
							</Button>
						</Box>
					</Box>
					<Divider />
					<Box
						sx={{
							maxHeight: 320,
							overflowY: 'auto',
							px: 2,
							py: 0.5,
						}}
					>
						{columns.map((column) => (
							<FormControlLabel
								key={column.key}
								sx={{ display: 'flex', mr: 0 }}
								control={
									<Checkbox
										size="small"
										checked={shown.has(column.key)}
										/* A table with no columns says nothing. */
										disabled={
											shown.has(column.key) &&
											visible.length === 1
										}
										onChange={(event) =>
											toggle(
												column.key,
												event.target.checked,
											)
										}
									/>
								}
								label={column.label}
							/>
						))}
					</Box>
				</Box>
			</Popover>
		</>
	);
}
